import { getCurrentAssignment } from './providerAssignments';
import { compareIsoDates, isDiagnosisActiveOnDate } from '../services/patientDiagnosisService';
import { getPatientRpmCareManagerIds } from './patientAccessScope';

/** Format a person as "Last, First". */
export function formatLastFirstName(person) {
  if (!person) return '';
  const last = (person.lastName || '').trim();
  const first = (person.firstName || '').trim();
  return [last, first].filter(Boolean).join(', ');
}

/** Format YYYY-MM-DD from string, Date, or Firestore Timestamp (local calendar date). */
export function formatReportDateYmd(value) {
  if (!value) return '';
  if (typeof value === 'string') return value.slice(0, 10);
  let date = null;
  if (typeof value.toDate === 'function') {
    try {
      date = value.toDate();
    } catch {
      date = null;
    }
  } else if (value instanceof Date) {
    date = value;
  }
  if (!date || Number.isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Last day of a reporting period as YYYY-MM-DD (local). */
export function periodEndYmdFromDate(end) {
  if (!end || Number.isNaN(end.getTime())) return new Date().toISOString().slice(0, 10);
  const y = end.getFullYear();
  const m = String(end.getMonth() + 1).padStart(2, '0');
  const d = String(end.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Whether the patient account was active as of asOfYmd (YYYY-MM-DD).
 * Uses deactivation.deactivatedAt / reactivation.reactivatedAt when present;
 * falls back to the current `active` flag when no deactivation is recorded.
 * A deactivation dated on asOfYmd counts as inactive that day.
 */
export function isPatientAccountActiveAsOf(patient, asOfYmd) {
  if (!patient || !asOfYmd) return false;
  const deactivatedAt = formatReportDateYmd(patient.deactivation?.deactivatedAt);
  if (!deactivatedAt) {
    return patient.active !== false;
  }
  if (deactivatedAt > asOfYmd) {
    return true;
  }
  const reactivatedAt = formatReportDateYmd(patient.reactivation?.reactivatedAt);
  if (!reactivatedAt) return false;
  return reactivatedAt >= deactivatedAt && reactivatedAt <= asOfYmd;
}

/** Resolve client short name from patient + lookup maps. */
export function resolveClientShortName(patient, clients, clientSites) {
  if (!patient) return '';
  if (patient.clientShortName) return patient.clientShortName;
  const clientMap = new Map((clients || []).map((c) => [c.id, c.clientShortName || c.clientName || '']));
  const siteMap = new Map((clientSites || []).map((s) => [s.id, s.clientId || '']));
  const siteId = patient.clientSiteId;
  if (!siteId) return '';
  const clientId = siteMap.get(siteId) || patient.clientId;
  if (!clientId) return '';
  return clientMap.get(clientId) || '';
}

/** Primary insurance entry on patient (index 0, or legacy insuranceId). */
export function getPrimaryInsuranceEntry(patient) {
  if (!patient) return null;
  const list = Array.isArray(patient.insurances) ? patient.insurances : [];
  if (list.length > 0 && list[0]?.insuranceId) return list[0];
  if (patient.insuranceId) return { insuranceId: patient.insuranceId };
  return null;
}

/** Primary payor name from patient + insurance catalog map. */
export function resolvePrimaryInsurancePayor(patient, insuranceById) {
  const entry = getPrimaryInsuranceEntry(patient);
  if (!entry?.insuranceId) return '';
  const ins = insuranceById?.get?.(entry.insuranceId) || insuranceById?.[entry.insuranceId];
  return ins?.payerName || '';
}

/** Primary payor + plan name (space-joined; plan omitted when empty). */
export function resolvePrimaryInsurancePayorPlan(patient, insuranceById) {
  const entry = getPrimaryInsuranceEntry(patient);
  if (!entry?.insuranceId) return '';
  const ins = insuranceById?.get?.(entry.insuranceId) || insuranceById?.[entry.insuranceId];
  if (!ins) return '';
  const payer = (ins.payerName || '').trim();
  const plan = (ins.planName || '').trim();
  return [payer, plan].filter(Boolean).join(' ');
}

/**
 * Earliest active diagnosis category as of a reference date (YYYY-MM-DD).
 * Falls back to icdCode when category is missing.
 */
export function resolvePrimaryCondition(patient, asOfDateStr) {
  const asOf = asOfDateStr || new Date().toISOString().slice(0, 10);
  const active = (Array.isArray(patient?.diagnoses) ? patient.diagnoses : [])
    .filter((dx) => isDiagnosisActiveOnDate(dx, asOf))
    .sort((a, b) => compareIsoDates(a.dxEffectiveDate, b.dxEffectiveDate));
  if (!active.length) return '';
  const primary = active[0];
  return (primary.category || primary.icdCode || '').toString().trim();
}

/** Provider name (Last, First) assigned as of reference date. */
export function resolveProviderName(patient, providersById, asOfDateStr) {
  const assignment = getCurrentAssignment(patient?.providerAssignments, asOfDateStr);
  if (!assignment?.providerId) return '';
  const provider = providersById?.get?.(assignment.providerId) || providersById?.[assignment.providerId];
  return formatLastFirstName(provider);
}

/** Primary care manager name (Last, First) from user map. */
export function resolvePrimaryCareManagerName(patient, userNameById) {
  const cmId = getPatientRpmCareManagerIds(patient)[0];
  if (!cmId) return '';
  return userNameById?.get?.(cmId) || userNameById?.[cmId] || '';
}
